import React from 'react';

import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from 'components/ui/dialog';

import type { FlowFieldChange } from './moduleChanges';

export type ModuleQuestionReview =
  | { status: 'unchanged' }
  | { status: 'loading' }
  | { status: 'ready'; diff: string[] }
  | { status: 'error' };

export type ModuleReviewDialogProps = {
  open: boolean;
  moduleName: string;
  courseCount: number;
  changes: FlowFieldChange[];
  questions: ModuleQuestionReview;
  saving: boolean;
  onConfirm: () => void;
  onClose: () => void;
};

/** Lists what a module save would change (settings from → to, and the question diff) before anything is saved. */
export const ModuleReviewDialog: React.FC<ModuleReviewDialogProps> = ({
  open,
  moduleName,
  courseCount,
  changes,
  questions,
  saving,
  onConfirm,
  onClose,
}) => (
  <Dialog open={open} onOpenChange={(next) => { if (!next && !saving) onClose(); }}>
    <DialogContent className="max-w-2xl" data-testid="fb-review-dialog">
      <DialogHeader>
        <DialogTitle>Review changes to {moduleName}</DialogTitle>
        <DialogDescription>
          {courseCount > 1
            ? `This module is in ${courseCount} courses. Saving changes it in all of them. Nothing is saved until you confirm.`
            : 'Nothing is saved until you confirm.'}
        </DialogDescription>
      </DialogHeader>
      <div className="max-h-[60vh] space-y-4 overflow-y-auto text-sm">
        <section aria-label="Module settings">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Module settings</p>
          {changes.length ? (
            <ul className="space-y-2" data-testid="fb-review-settings">
              {changes.map((change) => (
                <li key={change.field} className="rounded-xl bg-black/[0.03] px-3 py-2">
                  <p className="text-xs font-medium text-muted-foreground">{change.label}</p>
                  <p className="break-words">
                    <del className="text-muted-foreground">{change.from}</del>
                    <span aria-hidden="true"> → </span>
                    <span className="sr-only"> changes to </span>
                    <ins className="font-medium text-foreground no-underline">{change.to}</ins>
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted-foreground">No setting changes.</p>
          )}
        </section>
        <section aria-label="Questions">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Questions</p>
          {questions.status === 'unchanged' ? <p className="text-muted-foreground">No question changes.</p> : null}
          {questions.status === 'loading' ? <p className="text-muted-foreground">Checking question changes…</p> : null}
          {questions.status === 'error' ? (
            <p role="alert" className="text-destructive">
              Couldn’t preview the question changes. Saving still applies the order, required flags and membership shown in the editor.
            </p>
          ) : null}
          {questions.status === 'ready' ? (
            questions.diff.length ? (
              <ul className="ml-4 list-disc space-y-1" data-testid="fb-review-questions">
                {questions.diff.map((item, index) => (
                  <li key={`${item}-${index}`}>{item}</li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground">The question list matches the saved module.</p>
            )
          ) : null}
        </section>
      </div>
      <div className="flex justify-end gap-2">
        <Button size="auto" variant="ghost" disabled={saving} onClick={onClose}>
          Keep editing
        </Button>
        <Button size="auto" disabled={saving || questions.status === 'loading'} onClick={onConfirm}>
          {saving ? 'Saving...' : 'Confirm and save'}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

export default ModuleReviewDialog;
